// ============================================================
// useCustomerFlags — 予約が「同じ顧客からのもの」かを判定する
//
// 正: booking-platform/md/customers.md
// バックエンド: sys/customer/api.php?action=flags
//
// 顧客DB（予約データから自動生成・15分 cron）を引いて、
// 予約一覧や貸出返却リストに「この人は前にも来ている / 同日に複数台」の印を出す。
//
// 印を付けるべき予約コードだけを**一括で1回**取得する方式にしている:
//   - 対象は「有効な予約を2件以上持つ顧客」に限られるので小さい（実測 82件）
//   - ページング・フィルタ・並び替えの影響を受けない
//   - 行ごとに問い合わせない（リストは数百行になる）
// ============================================================
import { useState, useEffect, useCallback } from 'react';
import { LINE_SYNC_KEY } from '../config/instances';
import { logEvent } from '../lib/eventLog';

const API_URL = process.env.REACT_APP_CUSTOMER_API_URL
    || 'https://booking.trailernchu.co.jp/sys/customer/api.php';

/** 転送量を抑えるためキー名を短くしてある（api.php の flags と対応） */
export interface CustomerFlag {
    /** 顧客ID */
    c: number;
    /** 顧客の表示名 */
    n: string;
    /** 別々の日に来た回数（2以上ならリピーター） */
    v: number;
    /** 有効な予約の台数（v より大きければ同日に複数台） */
    r: number;
    /** 同一案件のグループコード（無ければ null） */
    g: string | null;
}

export interface CustomerMark {
    /** 別の日にも来ているお客様 */
    isRepeat: boolean;
    /** 同じ日に複数台（＝グループ） */
    isGroup: boolean;
    /** ツールチップに出す説明 */
    label: string;
    flag: CustomerFlag;
}

/** 予約1件分の印を組み立てる。印が不要なら null。 */
export function toCustomerMark(flag: CustomerFlag | undefined): CustomerMark | null {
    if (!flag) return null;
    const isRepeat = flag.v > 1;
    const isGroup = flag.r > flag.v;
    if (!isRepeat && !isGroup) return null;
    const parts: string[] = [];
    if (isRepeat) parts.push(`${flag.v}回目のご利用`);
    if (isGroup) parts.push(`同日${flag.r}台のグループ`);
    return { isRepeat, isGroup, label: `${flag.n}: ${parts.join(' / ')}`, flag };
}

// 画面をまたいで使い回す（貸出リストと予約一覧を行き来しても取り直さない）。
// 予約の追加・キャンセルは15分 cron で反映されるため、キャッシュは5分で捨てる。
let cache: { at: number; flags: Record<string, CustomerFlag> } | null = null;
const CACHE_MS = 5 * 60 * 1000;

export function useCustomerFlags() {
    const [flags, setFlags] = useState<Record<string, CustomerFlag>>(() => cache?.flags || {});

    const load = useCallback(async (force = false) => {
        if (!force && cache && Date.now() - cache.at < CACHE_MS) { setFlags(cache.flags); return; }
        try {
            const res = await fetch(`${API_URL}?action=flags&api_key=${encodeURIComponent(LINE_SYNC_KEY)}`);
            const json = await res.json();
            if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`);
            cache = { at: Date.now(), flags: json.flags || {} };
            setFlags(cache.flags);
        } catch (e: any) {
            // 印が出ないだけで業務は止まらない。ただし黙って消さない（md/loop-engineering.md）。
            logEvent({ action: 'customer.flags', target: 'api', result: 'fail', err: e?.message });
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    /** 予約コードから印を得る。印が不要なら null。 */
    const markOf = useCallback(
        (contactCode: string | null | undefined): CustomerMark | null =>
            (contactCode ? toCustomerMark(flags[contactCode]) : null),
        [flags],
    );

    return { flags, markOf, reloadCustomerFlags: () => load(true) };
}
